"use client";

/**
 * Staff store-app sign-in: brand, form, nothing else. No website link, no demo
 * CTA, no marketing footer — the app must never feel like the website.
 */

import { openExternalUrl } from "@v1/native";
import { useTranslations } from "next-intl";
import type { ReactElement } from "react";
import "./login.css";
import { LoginForm } from "./login-form";
import { CARD_BG, INK, PAGE_BG } from "./login-shared";
import { BrandLockupMark } from "./login-welcome-panel";

const FONT_STACK =
  "var(--font-inter), system-ui, -apple-system, 'Segoe UI', sans-serif";

function LegalLink({
  path,
  label,
}: {
  readonly path: string;
  readonly label: string;
}): ReactElement {
  return (
    <button
      type="button"
      onClick={() => {
        void openExternalUrl(new URL(path, window.location.origin).toString());
      }}
      style={{
        background: "none",
        border: 0,
        padding: "8px 6px",
        fontSize: 12.5,
        color: "rgba(11,15,25,0.5)",
        cursor: "pointer",
      }}
    >
      {label}
    </button>
  );
}

export function AppLoginScreen(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <div
      className="mklogin"
      style={{
        minHeight: "100dvh",
        fontFamily: FONT_STACK,
        background: PAGE_BG,
        color: INK,
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        padding:
          "calc(env(safe-area-inset-top) + 56px) 16px calc(env(safe-area-inset-bottom) + 12px)",
        boxSizing: "border-box",
      }}
    >
      <BrandLockupMark variant="light" size={34} />

      <div
        style={{
          width: "100%",
          maxWidth: 420,
          marginTop: 36,
          background: CARD_BG,
          border: "1px solid rgba(11,15,25,0.10)",
          borderRadius: 18,
          boxShadow:
            "0 18px 40px -24px rgba(11,15,25,0.26), 0 4px 12px -7px rgba(11,15,25,0.12)",
          padding: "28px 20px 26px",
        }}
      >
        <h1
          style={{
            fontWeight: 600,
            fontSize: 26,
            letterSpacing: "-0.02em",
            margin: 0,
          }}
        >
          {t("melde")}
        </h1>
        <LoginForm compact />
      </div>

      <p
        style={{
          maxWidth: 420,
          margin: "20px 8px 0",
          fontSize: 13,
          lineHeight: 1.5,
          color: "rgba(11,15,25,0.55)",
          textAlign: "center",
        }}
      >
        {t("appKeinZugang")}
      </p>

      <div style={{ flex: 1 }} />

      <AppLegalLinks />
    </div>
  );
}

/** Datenschutz · Impressum, opened over the app — shared by both store apps. */
export function AppLegalLinks(): ReactElement {
  const t = useTranslations("public.loginScreen");
  return (
    <nav style={{ display: "flex", gap: 4, justifyContent: "center" }}>
      <LegalLink path="/datenschutz" label={t("datenschutz")} />
      <LegalLink path="/impressum" label={t("impressum")} />
    </nav>
  );
}
